import { ClickAwayListener } from '@mui/material';
import React from 'react';
import { FieldPath, FieldValues, useController, useFormContext } from 'react-hook-form';
import get from 'lodash/get';
import { getEventValue } from '@cp/utils/form/getEventValue';
import clsx from 'clsx';
import { Box } from '../../../box';
import { Tooltip } from '../../../tooltip';
import { FieldLabel } from '../../label';
import { Root } from '../../fieldRoot';
import { FormFieldDictionaryItem, formFieldDictionary } from './dictionary';
import { FormFieldProps } from './model';
import { DEFAULT_FIELD_SIZE } from './base/model';

const defaultTransform = (v: unknown) => v;

export const FormField: <TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>(
  props: FormFieldProps<TName>,
) => React.ReactNode = ({
  type,
  name,
  hideErrorMessage,
  fieldContainerProps,
  fieldLabel,
  ref,
  transform: { in: transformIn = defaultTransform, out: transformOut = defaultTransform } = {},
  size = DEFAULT_FIELD_SIZE,
  ...restProps
}: FormFieldProps<string>) => {
  const fieldRef = React.useRef<HTMLInputElement>(null);
  const { control, formState, clearErrors } = useFormContext();
  const { field } = useController({ name, control });

  const pickedError = get(formState.errors, name);
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  const errorMessage = pickedError?.message ? String(pickedError?.message) : undefined;
  const hasError = Boolean(errorMessage);

  React.useEffect(() => {
    const newErrorVisibleState = Boolean(errorMessage);

    if (newErrorVisibleState && fieldRef.current) {
      fieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [errorMessage]);

  const { component: FieldComponent } = formFieldDictionary[type] as FormFieldDictionaryItem<string, typeof type>;

  const handleErrorTooltipClose = () => {
    clearErrors(name);
  };

  const { value, onChange, ref: fieldControllerRef, ...restFieldProps } = field;

  return (
    <Tooltip
      arrow
      colorSchema="error"
      disableFocusListener
      disableHoverListener
      disableTouchListener
      onClose={handleErrorTooltipClose}
      open={hasError && !hideErrorMessage}
      placement="bottom-start"
      title={
        <ClickAwayListener onClickAway={handleErrorTooltipClose}>
          <Box component="span" sx={{ p: 1, display: 'block' }}>
            {errorMessage ?? ''}
          </Box>
        </ClickAwayListener>
      }
    >
      <Root className={clsx(size)} ref={fieldRef} {...(fieldContainerProps as any)}>
        {fieldLabel && <FieldLabel size={size}>{fieldLabel}</FieldLabel>}
        <FieldComponent
          onChange={(e: React.ChangeEvent) => {
            const v = getEventValue(e);
            onChange(transformIn(v));
          }}
          value={transformOut(value)}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          {...(restFieldProps as any)}
          disabled={formState.isSubmitting}
          error={hasError && !hideErrorMessage}
          ref={(el: HTMLInputElement) => {
            fieldControllerRef(el);

            if (!ref) {
              return;
            }

            if (typeof ref === 'function') {
              // @ts-expect-error error after update
              ref(el);
            } else {
              ref.current = el;
            }
          }}
          size={size}
          type={type}
          {...(restProps as unknown as {})}
        />
      </Root>
    </Tooltip>
  );
};
